import React, { useState, useContext, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Message } from 'primereact/message'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Dialog } from 'primereact/dialog'
import { Growl } from 'primereact/growl'
import { Link, useLocation } from 'react-router-dom'
import { Context } from '../../Context'
import './styles.scss'

const ConfirmDialog = ({ onYesClick, isVisible, onHide }) => {
  const renderFooter = (onYesClick, onHide) => (
    <div>
      <Button label="Sí" icon="pi pi-check" onClick={onYesClick} />
      <Button label="No" icon="pi pi-times" onClick={onHide} className="p-button-secondary" />
    </div>
  )

  return (
    <Dialog
      header="¿Está seguro?"
      visible={isVisible}
      modal={true}
      onHide={onHide}
      footer={renderFooter(onYesClick, onHide)}
    >
      ¿Está seguro de eliminar el registro?
    </Dialog>
  )
}

export const DataListCourses = React.forwardRef(
  ({ menuItem, columns, listItems, bulkDelete, customActionButtons }, ref) => {
    const [dataTableValue, setDataTableValue] = useState(null)
    const [dataTableSelection, setDataTableSelection] = useState(null)
    const [globalFilter, setGlobalFilter] = useState(null)
    const [isLoading, setIsLoading] = useState(null)
    const [error, setError] = useState(null)
    const [itemToDelete, setItemToDelete] = useState(null)
    const location = useLocation()
    const growlRef = useRef(null)
    const { userLogged } = useContext(Context)


    const getList = async () => {
      try {
        setIsLoading(true)
        const data = await listItems()
        setDataTableValue(data)
      } catch (e) {
        setError(e.message)
      } finally {
        setIsLoading(false)
      }
    }

    const showGrowl = (severity, summary, detail) => {
      growlRef.current.show({
        severity,
        summary,
        detail,
      })
    }

    useEffect(() => {
      getList()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (location.state && location.state.success && growlRef) {
        showGrowl('success', 'Bien!', 'Registro guardado')
      }
    }, [location.state, growlRef])

    const deleteItems = async (ids) => {
      const previousElements = [...dataTableValue]

      const newData = dataTableValue.filter((d) => !ids.includes(d.id))
      setDataTableValue(newData)

      try {
        const result = await bulkDelete(ids)
        showGrowl('success', 'Bien!', result.message)
      } catch (e) {
        setDataTableValue(previousElements)
        showGrowl('error', 'Ups!', 'Ocurrió un error al eliminar')
      }
    }

    const onDeleteButtonClick = () => {
      const id = itemToDelete.id
      setItemToDelete(null)
      deleteItems([id])
    }

    const onBulkDeleteButtonClick = () => {
      if (dataTableSelection) {
        const ids = dataTableSelection.map((r) => r.id)
        deleteItems(ids)
      }
      else {
        showGrowl('warn', 'Ups!', 'Selecciona al menos un programa')
      }
    }

    const isStudent = () => {
      const rolLogged = userLogged.role;
      if (rolLogged === 'U') {
        return true
      }
    }

    const isTeacher = () => {
      const rolLogged = userLogged.role;
      if (rolLogged === 'T') {
        return true
      }
    }

    const isCreator = () => {
      const rolLogged = userLogged.role;
      if (rolLogged === 'C') {
        return true
      }
    }

    const isAdmin = () => {
      const rolLogged = userLogged.role;
      if (rolLogged === 'S') {
        return true
      }
    }

    const statusBodyTemplate = (rowData) => {
      const status = rowData.status;
      const statusText = status === 1
        ? 'Activo'
        : 'Inactivo';

      return (
        <span>
          {statusText}
        </span>
      );
    };


    const actionBodyTemplate = (item) => {
      const CustomButtons = customActionButtons ? customActionButtons : () => <div />
      if (isAdmin() || isCreator()) {
        return (
          <div className="action-buttons">

            <Link to={`${menuItem.link}/edit/${item.id}`}>
              <Button
                type="button"
                icon="pi pi-pencil"
                className="p-button-primary action-button"
                tooltip="Actualizar"
                tooltipOptions={{ position: 'top' }}

              ></Button>
            </Link>

            <Link to={`${menuItem.link}/uses/${item.id}`}>
              <Button
                type="button"
                icon="pi pi-chart-bar"
                className="p-button-info action-button"
                tooltip="Usos"
                tooltipOptions={{ position: 'top' }}
              ></Button>
            </Link>

            <CustomButtons row={item} />

            <Button
              type="button"
              icon="pi pi-trash"
              className="p-button-danger action-button"
              tooltip="Eliminar"
              tooltipOptions={{ position: 'top' }}

              onClick={() => setItemToDelete(item)}
            ></Button>
          </div>
        )

      } else if (isTeacher()) {
        return (<div className="action-buttons">
          <Link to={`${menuItem.link}/edit/${item.id}`}>
            <Button
              type="button"
              icon="pi pi-pencil"
              className=" p-button-primary action-button"
              tooltip="Asignar estudiantes"
            ></Button>
          </Link>

          <Link to={`${menuItem.link}/uses/${item.id}`}>
            <Button
              type="button"
              icon="pi pi-chart-bar"
              className="p-button-info action-button"
              tooltip="Usos"
              tooltipOptions={{ position: 'top' }}
            ></Button>
          </Link>

          <CustomButtons row={item} />
        </div>)
      } else {
        return (
          <div className="action-buttons">
            <CustomButtons row={item} />
          </div>
        )
      }
    }


    const TopMenuActions = () => {
      if (!(isStudent() || isTeacher())) {
        return (
          <div className="top-menu-actions">
            <Link to={`${menuItem.link}/create`}>
              <Button label="Crear" icon="pi pi-plus" className="top-menu-button" />
            </Link>

            {bulkDelete && (
              <Button
                label="Borrado"
                icon="pi pi-trash"
                className="p-button-danger top-menu-button"
                onClick={onBulkDeleteButtonClick}
              />
            )}
          </div>
        );
      }
    }


    const renderHeader = (title) => {
      return (
        <>
          <div className="p-datatable-title">Lista de {title}</div>
          <div>
            <InputText
              type="search"
              onInput={(e) => setGlobalFilter(e.target.value)}
              placeholder="Búsqueda"
            />
          </div>
        </>
      )
    }

    const header = renderHeader(menuItem.title)

    const columnList = [...columns]
    columnList.unshift({
      field: 'selector',
      selectionMode: 'multiple',
      style: { width: '3em' },
    })

    if (isStudent() || isAdmin() || isCreator() || isTeacher()) {
      columnList.push({
        field: 'actions',
        header: 'Acciones',
        sortable: false,
        body: actionBodyTemplate,
        headerStyle: { width: '8em' },
        bodyStyle: { textAlign: 'center', overflow: 'visible' },
      })
    }

    columnList.forEach(col => {
      if (col.field === 'status') {
        col.body = statusBodyTemplate;
      }
    });


    const dynamicColumns = columnList.map((col) => {
      if (col.field === 'actions') {
        return (
          <Column
            key={col.field}
            header="Acciones"
            body={actionBodyTemplate}
            headerStyle={{ width: '15em' }}
            bodyStyle={{ textAlign: 'center', overflow: 'visible' }}
          />
        );
      } else {
        return <Column key={col.field} {...col} />;
      }
    })

    return (
      <div className="p-grid">
        <Growl ref={growlRef}></Growl>

        <ConfirmDialog
          onYesClick={onDeleteButtonClick}
          isVisible={itemToDelete !== null}
          onHide={() => setItemToDelete(null)}
        ></ConfirmDialog>

        <div className="p-col-12">
          <div className="card card-w-title custom-title">
            <div className="datatable-title">
              <h1>
                <i className={menuItem.icon}></i> {menuItem.title}
              </h1>
              {TopMenuActions()}


            </div>

            {isLoading ? (
              <div className="loading-container">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '4em' }}></i>
              </div>
            ) : error ? (
              <Message severity="error" text={error}></Message>
            ) : (
              <DataTable
                ref={ref}
                value={dataTableValue}
                paginatorPosition="both"
                header={header}
                className="p-datatable-custom"
                globalFilter={globalFilter}
                paginator={true}
                dataKey="id"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink  RowsPerPageDropdown"
                rowsPerPageOptions={[10, 25, 50]}
                emptyMessage="No se encontraron registros"
                rows={50}
                responsive={true}
                scrollable={true}
                scrollHeight="400px"
                selection={dataTableSelection}
                onSelectionChange={(event) => setDataTableSelection(event.value)}
              >
                {dynamicColumns}
              </DataTable>
            )}
          </div>
        </div>
      </div>
    )
  }
)

DataListCourses.propTypes = {
  menuItem: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  listItems: PropTypes.func.isRequired,
  bulkDelete: PropTypes.func.isRequired,
  customActionButtons: PropTypes.func,
}
